import * as React from "react";
import {
    getReactionPreview,
    REACTION_EMOJIS,
    type AttributedReaction,
    type ReactionUser,
    type ReactionCount,
    type ReactionType,
} from "frontend-components/lib/reactions";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "frontend-components/components/ui/hover-card";
import {
    ReactionUserList,
    ReactionUserListContainer,
} from "frontend-components/components/ui/reaction-user-list";
import { cn } from "frontend-components/lib/utils";

interface ReactionPreviewProps extends React.ComponentProps<"span"> {
    reactionCounts: readonly ReactionCount[];
    labels: Record<ReactionType, string>;
    maxVisible?: number;
    details?: {
        attributedReactions?: readonly AttributedReaction[];
        getUserAvatarUrl?: (user: ReactionUser) => string | undefined;
        isLoading: boolean;
        isError: boolean;
        labels: { loading: string; error: string; empty: string };
        onOpenChange: (open: boolean) => void;
    };
}

function ReactionPreview({
    reactionCounts,
    labels,
    maxVisible = 2,
    className,
    details,
    ...props
}: ReactionPreviewProps) {
    const [isOpen, setIsOpen] = React.useState(false);
    const { sortedReactions, visibleReactions, overflowCount } = getReactionPreview(
        reactionCounts,
        maxVisible,
    );

    if (sortedReactions.length === 0) {
        return null;
    }

    const preview = (
        <span
            data-slot="reaction-preview"
            className={cn("inline-flex shrink-0 items-center whitespace-nowrap", className)}
            {...props}
        >
            <span aria-hidden="true" className="inline-flex items-center gap-2">
                {visibleReactions.map(({ type, count }) => (
                    <span key={type} className="inline-flex items-center gap-1">
                        <span>{REACTION_EMOJIS[type]}</span>
                        <span>{count}</span>
                    </span>
                ))}
                {overflowCount > 0 && <span>+{overflowCount}</span>}
            </span>
            <span className="sr-only">
                {sortedReactions.map(({ type, count }) => `${labels[type]}: ${count}`).join(", ")}
            </span>
        </span>
    );

    if (!details) {
        return preview;
    }

    const attributedReactionsByType = new Map(
        details.attributedReactions?.map((reaction) => [reaction.type, reaction]),
    );
    const users = sortedReactions.flatMap(({ type }) =>
        (attributedReactionsByType.get(type)?.users ?? []).map((user) => ({
            user,
            emoji: REACTION_EMOJIS[type],
        })),
    );

    return (
        <HoverCard
            open={isOpen}
            onOpenChange={(open, eventDetails) => {
                if (open && eventDetails.reason !== "trigger-hover") {
                    return;
                }
                setIsOpen(open);
                details.onOpenChange(open);
            }}
        >
            <HoverCardTrigger render={preview} />
            <HoverCardContent align="end" className="pointer-coarse:hidden">
                <ReactionUserListContainer>
                    {details.isLoading ? (
                        <p className="px-1 py-2 text-body-sm text-compendia-muted">
                            {details.labels.loading}
                        </p>
                    ) : details.isError ? (
                        <p className="px-1 py-2 text-body-sm text-compendia-muted">
                            {details.labels.error}
                        </p>
                    ) : users.length > 0 ? (
                        <ReactionUserList
                            items={users}
                            getUserAvatarUrl={details.getUserAvatarUrl}
                        />
                    ) : (
                        <p className="px-1 py-2 text-body-sm text-compendia-muted">
                            {details.labels.empty}
                        </p>
                    )}
                </ReactionUserListContainer>
            </HoverCardContent>
        </HoverCard>
    );
}

export { ReactionPreview };
export type { ReactionPreviewProps };
